import { createStore } from "owa-satcheljs";
import type { ReengagementOpenEmailCta } from "owa-personal-os-types";
import type { MailboxInfo } from "owa-client-types";
export type MyOutlookTab = "today" | "mirror" | "automations";

export interface MyOutlookViewState {
    isFullPageShowing: boolean;
    initialTab: MyOutlookTab;
    isSectionHidden: boolean;
    // Whether the schedule timeline is visible on the full page. Collapsed by
    // default on load; the user's last explicit show/hide is hydrated from
    // localStorage on first read (see getIsScheduleVisible). User toggles it
    // via the calendar-icon toggle.
    isScheduleVisible: boolean;
    // Whether all-day events are shown in the MyOutlook Today calendar widget
    // grid. Hidden by default; the user's last explicit choice is hydrated from
    // localStorage on first read (see getIsAllDayEventsVisible). Widget-scoped.
    isAllDayEventsVisible: boolean;
    // Snapshot of `isScheduleVisible` captured when a detail pane opens so
    // close can restore the user's pre-open preference. Never persisted.
    scheduleVisibleBeforeDetailPane: boolean | null;
    // Actionable-item ids to highlight, driven by hovering / focusing a
    // briefing narrative sentence (matched against the sentence's citations).
    // Purely transient view state — never persisted.
    highlightedActionableItemIds: string[];
    // One-shot handoff from the folder-pane re-engagement card's "Open email"
    // CTA. Today consumes and clears it on mount.
    pendingReengagementAction: ReengagementOpenEmailCta | null;
    // Briefing item whose simplified event form was popped out to full compose.
    // Consumed when that compose commits an event, so an abandoned compose never
    // marks the item done. Never persisted.
    pendingMeetingHandoff: PendingMeetingHandoff | null;
}

export interface PendingMeetingHandoff {
    itemId: string;
    mailboxInfo: MailboxInfo;
    briefingId?: string;
}

const getMyOutlookViewState: () => MyOutlookViewState = createStore<MyOutlookViewState>("MyOutlookViewState", {
    isFullPageShowing: false,
    initialTab: "today" as MyOutlookTab,
    isSectionHidden: false,
    isScheduleVisible: false,
    isAllDayEventsVisible: false,
    scheduleVisibleBeforeDetailPane: null,
    highlightedActionableItemIds: [],
    pendingReengagementAction: null,
    pendingMeetingHandoff: null,
});

export default getMyOutlookViewState;
